import schema from './siteBackgroundDefaults.json';
import { SITE_THEME_IDS, type SiteThemeId } from './siteTheme';

export type BackgroundEffect = 'neurons' | 'points';
export type BackgroundColors = { fiber: string; signal: string; haze: string };
export type BackgroundConfig = {
  effect: BackgroundEffect;
  count: number;
  depth: number;
  blur: number;
  colors: Record<SiteThemeId, BackgroundColors>;
};
export type SiteBackgroundSettings = { schema_version: 1; revision: number; config: BackgroundConfig };
export const BACKGROUND_LIMITS = schema.limits;
export const BACKGROUND_COLOR_LABELS = { fiber: 'Цвет волокон', signal: 'Цвет импульсов', haze: 'Цвет глубины' } as const;

export function defaultBackgroundConfig(): BackgroundConfig {
  return JSON.parse(JSON.stringify(schema.defaults)) as BackgroundConfig;
}

export function validateBackgroundConfig(value: unknown): string | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 'Ожидается конфигурация фона';
  const config = value as Record<string, unknown>;
  if (Object.keys(config).some(key => !['effect', 'count', 'depth', 'blur', 'colors'].includes(key))) return 'Неизвестный параметр фона';
  if (typeof config.effect !== 'string' || !['neurons', 'points'].includes(config.effect)) return 'Выберите доступный эффект';
  for (const key of ['count', 'depth', 'blur'] as const) {
    const [min, max] = BACKGROUND_LIMITS[key];
    if (typeof config[key] !== 'number' || !Number.isInteger(config[key]) || config[key] < min || config[key] > max) return `Параметр ${key}: целое число от ${min} до ${max}`;
  }
  const colors = config.colors as Record<string, unknown> | undefined;
  if (!colors || typeof colors !== 'object' || Array.isArray(colors) || Object.keys(colors).some(key => !(SITE_THEME_IDS as readonly string[]).includes(key))) return 'Некорректные темы фона';
  for (const id of SITE_THEME_IDS) {
    const palette = colors[id] as Record<string, unknown> | undefined;
    if (!palette || typeof palette !== 'object' || Array.isArray(palette) || Object.keys(palette).some(key => !Object.keys(BACKGROUND_COLOR_LABELS).includes(key))) return 'Некорректная палитра фона';
    for (const key of Object.keys(BACKGROUND_COLOR_LABELS)) {
      if (typeof palette[key] !== 'string' || palette[key].length !== 7 || !/^#[0-9a-f]{6}$/i.test(palette[key])) return 'Цвет должен иметь формат #RRGGBB';
    }
  }
  return null;
}

export function normalizeBackgroundSettings(value: unknown): SiteBackgroundSettings {
  const block = value as Partial<SiteBackgroundSettings> | null;
  if (block?.schema_version === 1 && Number.isInteger(block.revision) && block.revision! >= 0 && block.revision! < 2147483647 && !validateBackgroundConfig(block.config)) return { schema_version: 1, revision: block.revision!, config: block.config! };
  return { schema_version: 1, revision: 0, config: defaultBackgroundConfig() };
}
